import type { FuelVehicleTypeKey, VendorOption } from '../types';

/**
 * Map resource vehicle type text (Subcontractor Master) to on/off-road.
 * Mirrors server `inferFuelVehicleTypeFromText`.
 */
export function inferFuelVehicleTypeFromText(
  raw: string | null | undefined,
): FuelVehicleTypeKey | null {
  const primary = String(raw ?? '').toLowerCase();
  if (/off[\s_-]?road/.test(primary) || primary.includes('offroad')) {
    return 'OFF_ROAD';
  }
  if (/on[\s_-]?road/.test(primary) || primary.includes('onroad')) {
    return 'ON_ROAD';
  }
  return null;
}

/**
 * HE-F17: fuel-surcharge eligibility defaults from the resource's vehicle type.
 *
 * On-Road / Off-Road → eligible (`true`). Explicit N/A → ineligible (`false`).
 * Missing/unknown → `null` (ops must set explicitly).
 */
export function defaultFuelSurchargeEligibleFromVehicleType(
  vehicleType: string | null | undefined,
): boolean | null {
  const trimmed = (vehicleType ?? '').trim();
  if (!trimmed) return null;
  if (inferFuelVehicleTypeFromText(trimmed)) return true;
  const lower = trimmed.toLowerCase();
  if (lower === 'n/a' || lower === 'na' || lower === 'none') return false;
  return null;
}

/**
 * Legacy vendor-level flag when present on {@link VendorOption} (tests / future API).
 * Prefer {@link defaultFuelSurchargeEligibleFromVehicleType} for catalogue resources.
 */
export function defaultFuelSurchargeEligible(
  vendor: VendorOption | null | undefined,
): boolean | null {
  if (vendor && typeof vendor.fuelSurchargeEligible === 'boolean') {
    return vendor.fuelSurchargeEligible;
  }
  return null;
}

export function patchFuelSurchargeFromVendor<T extends { fuelSurchargeEligible: boolean | null }>(
  row: T,
  vendor: VendorOption | null | undefined,
): T {
  return { ...row, fuelSurchargeEligible: defaultFuelSurchargeEligible(vendor) };
}
